import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { Bell } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { api } from '@/lib/api';
import { cn } from '@/lib/utils';
import { sound } from '@/lib/sound';

interface Note { id: string; kind: string; title: string; body: string; link: string; read: boolean; createdAt: string }

const GROUPS: [string, string, (k: string) => boolean][] = [
  ['all', 'All', () => true],
  ['alert', 'Alerts', (k) => k.startsWith('alert.')],
  ['mfg', 'Production', (k) => k.startsWith('mfg.')],
  ['ins', 'Installation', (k) => k.startsWith('ins.')],
];

const ago = (iso: string) => {
  const m = Math.round((Date.now() - Date.parse(iso)) / 60000);
  return m < 1 ? 'just now' : m < 60 ? `${m} min ago` : m < 1440 ? `${Math.round(m / 60)} h ago` : `${Math.round(m / 1440)} d ago`;
};

/** Bell in the header: notices for the signed-in user (e.g. an order that will now be late). */
export function NotificationsButton() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [group, setGroup] = useState('all');
  const box = useRef<HTMLDivElement>(null);
  const { data } = useQuery({ queryKey: ['notifications'], queryFn: () => api<{ unread: number; items: Note[] }>('/notifications'), refetchInterval: 120_000 });
  useEffect(() => {
    if (!open) return;
    const close = (e: MouseEvent) => !box.current?.contains(e.target as Node) && setOpen(false);
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [open]);
  const read = async (ids: string[] | 'all') => {
    await api('/notifications/read', { method: 'POST', body: { ids } }).catch(() => undefined);
    await qc.invalidateQueries({ queryKey: ['notifications'] });
  };
  const n = data?.unread ?? 0;
  // Something new arrived since the last check (not on the first load): a soft chime.
  const seen = useRef<number | null>(null);
  useEffect(() => {
    if (data == null) return;
    if (seen.current != null && n > seen.current) sound.notify();
    seen.current = n;
  }, [data, n]);
  return (
    <div ref={box} className="relative">
      <button type="button" onClick={() => setOpen(!open)} className={cn('fx-tip relative flex size-10 items-center justify-center rounded-xl border border-border bg-surface text-fg-muted transition-all duration-200 hover:-translate-y-px hover:border-accent hover:text-primary hover:shadow-(--shadow-card) active:scale-95', open && 'border-accent text-primary')} aria-label={n ? `Notifications, ${n} unread` : 'Notifications'} aria-expanded={open} data-tip="Notifications">
        <Bell className={cn('size-[18px]', n > 0 && 'fx-ping')} />
        {n > 0 && <span key={n} className="tabular fx-pop absolute -top-1.5 -right-1.5 flex h-[18px] min-w-[18px] items-center justify-center rounded-full bg-primary px-1 text-[10px] font-bold text-white shadow-[0_0_0_2px_var(--color-surface)]">{n > 99 ? '99+' : n}</span>}
      </button>
      {open && (
        <div className="fx-menu absolute right-0 z-50 mt-2 w-[360px] max-w-[calc(100vw-1.5rem)] origin-top-right overflow-hidden rounded-2xl border border-border bg-surface/95 shadow-(--shadow-pop) backdrop-blur-xl" data-state="open">
          <div className="flex items-center justify-between border-b border-border px-4 py-2.5">
            <span className="text-[13px] font-semibold">Notifications</span>
            {n > 0 && <button type="button" onClick={() => void read('all')} className="text-[12px] text-primary hover:underline">Mark all read</button>}
          </div>
          <div className="flex gap-1 border-b border-border px-3 py-2">
            {GROUPS.map(([k, l, f]) => {
              const unread = (data?.items ?? []).filter((x) => !x.read && f(x.kind)).length;
              return <button key={k} type="button" onClick={() => setGroup(k)} className={cn('flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11.5px] transition-colors duration-150', group === k ? 'border-primary bg-primary-soft text-primary' : 'border-border text-fg-muted')}>{l}{unread > 0 && <span className="rounded-full bg-primary px-1 text-[10px] font-bold text-white">{unread}</span>}</button>;
            })}
          </div>
          <ul className="max-h-[420px] divide-y divide-border overflow-y-auto">
            {!(data?.items ?? []).filter((x) => GROUPS.find((g) => g[0] === group)![2](x.kind)).length && <li className="px-4 py-6 text-center text-[12.5px] text-fg-subtle">Nothing here.</li>}
            {data?.items.filter((x) => GROUPS.find((g) => g[0] === group)![2](x.kind)).map((x) => (
              <li key={x.id}>
                <button type="button" onClick={() => { void read([x.id]); setOpen(false); if (x.link) void navigate({ to: x.link }); }} className={cn('block w-full px-4 py-2.5 text-left hover:bg-surface-2', !x.read && 'bg-primary/5')}>
                  <p className={cn('text-[12.5px]', !x.read && 'font-semibold')}>{!x.read && <span className="mr-1.5 inline-block size-2 rounded-full bg-primary" />}{x.title}</p>
                  {x.body && <p className="mt-0.5 line-clamp-4 text-[12px] whitespace-pre-line text-fg-muted">{x.body}</p>}
                  <p className="mt-0.5 text-[11px] text-fg-subtle">{ago(x.createdAt)}</p>
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
